import { SquadCodeSchema } from '@voll-intelligence/domain';

import { t } from '@web/i18n';
import {
  AppIcons,
  Button,
  Card,
  Scaffold,
  toScreamingSnake,
  useGoBack,
  useOrgContext,
} from '@web/ui/core';
import { FormTextField } from '@web/ui/core/molecules/form';
import { fireAndForget, twcn } from '@web/ui/core/utils';
import { useSquadEditor } from '@web/ui/squads/hooks';

type FormType = ReturnType<typeof useSquadEditor>['form'];

export function SquadIdentityPage(): React.JSX.Element | null {
  const { mode, isReady, form, projectId, projectCode } = useSquadEditor();
  const { orgSlug } = useOrgContext();
  const onCancel = useGoBack({
    to: projectId ? '/orgs/$orgSlug/project/$projectId/squads' : '/orgs/$orgSlug/overview',
    params: projectId ? { orgSlug, projectId } : { orgSlug },
  });

  if (!isReady) return null;

  const prefix = projectCode ? `${projectCode}.` : '';

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        fireAndForget(form.handleSubmit());
      }}
    >
      <Scaffold
        body={
          <>
            <Preview form={form} prefix={prefix} />
            <Fields form={form} mode={mode} prefix={prefix} />
            <Actions form={form} mode={mode} onCancel={onCancel} />
          </>
        }
      />
    </form>
  );
}

function Preview({
  form,
  prefix,
}: Readonly<{ form: FormType; prefix: string }>): React.JSX.Element {
  return (
    <form.Subscribe selector={(s) => [s.values.name, s.values.code] as const}>
      {([name, code]) => (
        <div className="mb-5.5">
          <Card>
            <div className="grid grid-cols-identity-hero items-center gap-3.5 p-4 pr-5">
              <span className="inline-flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-md bg-primary-tint text-primary-ink">
                <AppIcons name="users-four" size={26} />
              </span>
              <div className="flex min-w-0 flex-col gap-1">
                <p
                  className={twcn(
                    'm-0 truncate font-serif text-h-display font-normal leading-display tracking-display-tight',
                    name.trim() ? '' : 'italic text-faint-foreground',
                  )}
                >
                  {name.trim() || t('squad.editor.identity.previewPlaceholder')}
                </p>
                {code ? (
                  <span className="inline-flex h-5 w-fit items-center gap-1 rounded-sm bg-muted px-1.5 font-mono text-card-id text-foreground">
                    <AppIcons name="hash" size={10} />
                    {prefix}
                    {code}
                  </span>
                ) : null}
              </div>
            </div>
          </Card>
        </div>
      )}
    </form.Subscribe>
  );
}

function Fields({
  form,
  mode,
  prefix,
}: Readonly<{
  form: FormType;
  mode: 'create' | 'edit';
  prefix: string;
}>): React.JSX.Element {
  const propagateNameToCode = (next: string): void => {
    if (mode !== 'create') return;
    const codeField = form.getFieldMeta('code');
    if (codeField?.isDirty) return;
    // dontUpdateMeta keeps isDirty/isTouched untouched — otherwise the
    // very first programmatic setFieldValue would mark the code field
    // dirty and freeze subsequent auto-derivation from the name field.
    form.setFieldValue('code', toScreamingSnake(next), { dontUpdateMeta: true });
  };

  const validateName = (value: string): string | undefined =>
    value.trim().length === 0 ? t('squad.editor.fields.name.errorRequired') : undefined;

  const validateCode = (value: string): string | undefined => {
    if (mode === 'edit') return undefined;
    if (!value) return t('squad.editor.fields.code.errorRequired');
    return SquadCodeSchema.safeParse(value).success
      ? undefined
      : t('squad.editor.fields.code.errorFormat');
  };

  return (
    <>
      <form.Field
        name="name"
        validators={{
          onChange: ({ value }) => validateName(value),
          onBlur: ({ value }) => validateName(value),
        }}
      >
        {(field) => (
          <FormTextField
            field={field}
            label={t('squad.editor.fields.name.label')}
            required
            autoFocus
            autoComplete="off"
            placeholder={t('squad.editor.fields.name.placeholder')}
            onAfterChange={propagateNameToCode}
          />
        )}
      </form.Field>

      <form.Field
        name="code"
        validators={{
          onChange: ({ value }) => validateCode(value),
          onBlur: ({ value }) => validateCode(value),
        }}
      >
        {(field) => {
          const isLocked = mode === 'edit';
          return (
            <FormTextField
              field={field}
              label={t('squad.editor.fields.code.label')}
              required={!isLocked}
              labelEndAdornment={
                isLocked ? (
                  <span className="inline-flex items-center gap-1 font-mono text-tiny uppercase tracking-eyebrow-narrow text-faint-foreground">
                    <AppIcons name="lock" size={11} />
                    {t('squad.editor.fields.code.immutableTag')}
                  </span>
                ) : null
              }
              autoComplete="off"
              spellCheck={false}
              readOnly={isLocked}
              disabled={isLocked}
              placeholder={t('squad.editor.fields.code.placeholder')}
              transformValue={toScreamingSnake}
              mono
              startAdornment={prefix || 'PROJETO.'}
              hint={
                isLocked
                  ? t('squad.editor.fields.code.hintEdit')
                  : t('squad.editor.fields.code.hintCreate')
              }
            />
          );
        }}
      </form.Field>
    </>
  );
}

function Actions({
  form,
  mode,
  onCancel,
}: Readonly<{
  form: FormType;
  mode: 'create' | 'edit';
  onCancel: () => void;
}>): React.JSX.Element {
  return (
    <div className="mt-8 flex items-center gap-2 border-t border-border pt-4">
      <span className="font-mono text-card-id tracking-eyebrow-narrow text-faint-foreground">
        {t('squad.editor.actions.escHint')}
      </span>
      <span className="flex-1" />
      <form.Subscribe selector={(s) => [s.isSubmitting, s.canSubmit] as const}>
        {([isSubmitting, canSubmit]) => (
          <>
            <Button variant="outline" size="lg" onClick={onCancel} disabled={isSubmitting}>
              {t('squad.editor.actions.cancel')}
            </Button>
            <Button type="submit" variant="primary" size="lg" disabled={!canSubmit || isSubmitting}>
              <AppIcons name={mode === 'edit' ? 'check' : 'plus'} size={14} />
              <span>
                {mode === 'edit'
                  ? t('squad.editor.actions.save')
                  : t('squad.editor.actions.create')}
              </span>
            </Button>
          </>
        )}
      </form.Subscribe>
    </div>
  );
}
